import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Paper,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import Loading from '../../../../components/loading';
import SuccessMessage from '../../../../components/successMessage';
import ErrorMessage from '../../../../components/errorMessage';
import PrescriptionModalRouter from '../prescriptionModal/prescriptionModalRouter';
import { usePrescriptions } from '../hooks/usePrescriptions';
import { usePrescriptionTemplate } from '../hooks/usePrescriptionTemplate';
import SignHealthDocumentModal from '../../../../components/signHealthDocumentModal/signHealthDocumentModal';
import { useSignedDocumentActions } from '../../../../hooks/useSignedDocumentActions';
import { useSignPrescription } from '../hooks/useSignPrescription';
import { useSignedPrescriptionDocument } from '../hooks/useSignedPrescriptionDocument';
import {
  CONTROLLED_SIGNATURE_UNAVAILABLE,
  formatPrescriptionSummary,
  hasControlledMedicines,
} from '../prescriptionSignature';

export default function PrescriptionListWeb() {
  const { patientId } = useParams();

  const {
    prescriptions,
    isLoading,
    error: prescriptionsError,
    refetch: refetchPrescriptions,
  } = usePrescriptions(patientId);
  const {
    templateMode,
    clinicTemplate,
    dentistTemplates,
    isLoading: isTemplateLoading,
    error: templateError,
  } = usePrescriptionTemplate();

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [expandedPrescriptionId, setExpandedPrescriptionId] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);

  const { sign: signPrescription } = useSignPrescription();
  const { fetchUrl: fetchSignedPrescriptionUrl } = useSignedPrescriptionDocument();
  const {
    documentToSign: prescriptionToSign,
    openSignModal,
    closeSignModal,
    onSigned,
    openSignedDocument,
    isOpeningSignedDocument,
  } = useSignedDocumentActions({
    fetchSignedDocumentUrl: (prescriptionId) =>
      fetchSignedPrescriptionUrl(patientId, prescriptionId),
    refetch: refetchPrescriptions,
    onSuccess: (message) => {
      setShowSuccessMessage(true);
      setSuccessMessage(message);
    },
    onError: (message) => {
      setShowErrorMessage(true);
      setErrorMessage(message);
    },
  });

  useEffect(() => {
    const err = prescriptionsError || templateError;
    if (err) {
      setErrorMessage(err);
      setShowErrorMessage(true);
    }
  }, [prescriptionsError, templateError]);

  const onSavePrescription = (returnFromApi) => {
    if (returnFromApi.data) {
      setShowSuccessMessage(true);
      setSuccessMessage(returnFromApi.data);
    } else {
      setShowErrorMessage(true);
      setErrorMessage(returnFromApi.response?.data);
    }
    setModalOpen(false);
    refetchPrescriptions();
  };

  const printPrescription = (prescription) => {
    const { receituarioId, dentistId } = prescription;
    const hasControlledMedicines = (prescription.medicamentos ?? []).some((m) => m.controlled);

    if (hasControlledMedicines) {
      window.open(
        `/prescription-controlled-medicines-to-print/${patientId}/${receituarioId}`,
        '_blank',
      );
      return;
    }

    const hasClinicTemplate = clinicTemplate?.urlFromTemplate;
    const hasPerDentistTemplates = dentistTemplates?.length > 0;

    if (templateMode === 'CLINIC' && hasClinicTemplate) {
      const params = new URLSearchParams({
        templateUrl: clinicTemplate.urlFromTemplate,
      });
      window.open(
        `/prescription-to-print-template/${patientId}/${receituarioId}?${params}`,
        '_blank',
      );
    } else if (templateMode === 'PER_DENTIST' && hasPerDentistTemplates) {
      const dentistTemplate = dentistTemplates.find((t) => t.dentistId === dentistId);
      if (dentistTemplate?.urlFromTemplate) {
        const params = new URLSearchParams({ templateUrl: dentistTemplate.urlFromTemplate });
        window.open(
          `/prescription-to-print-template/${patientId}/${receituarioId}?${params}`,
          '_blank',
        );
      } else {
        window.open(`/prescription-to-print/${patientId}/${receituarioId}`, '_blank');
      }
    } else {
      window.open(`/prescription-to-print/${patientId}/${receituarioId}`, '_blank');
    }
  };

  return (
    <>
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Loading
          isToHidden={
            isLoading === false && isTemplateLoading === false && isOpeningSignedDocument === false
          }
        />
        {showSuccessMessage && (
          <SuccessMessage
            message={successMessage}
            show={showSuccessMessage}
            setShow={setShowSuccessMessage}
          />
        )}
        {showErrorMessage && (
          <ErrorMessage
            message={errorMessage}
            show={showErrorMessage}
            setShow={setShowErrorMessage}
          />
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Receituários
          </Typography>
          <Button variant="outlined" onClick={() => setModalOpen(true)}>
            Adicionar Receituário
          </Button>
        </Box>

        {prescriptions.length === 0 ? (
          <Typography align="center" sx={{ py: 4 }}>
            Nenhuma receita cadastrada ainda.
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Data</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Profissional</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Assinatura digital</TableCell>
                  <TableCell sx={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {prescriptions.map((p) => {
                  const isExpanded = expandedPrescriptionId === p.receituarioId;
                  const toggleExpand = () =>
                    setExpandedPrescriptionId(isExpanded ? null : p.receituarioId);
                  return (
                    <React.Fragment key={p.receituarioId}>
                      <TableRow hover sx={{ cursor: 'pointer' }} onClick={toggleExpand}>
                        <TableCell>{p.data}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            <span>{p.nomeDoDentista}</span>
                            {(p.medicamentos ?? []).some((m) => m.controlled) && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography
                                  variant="caption"
                                  color="warning.main"
                                  sx={{ fontWeight: 500, lineHeight: 1.3 }}
                                >
                                  Receita de Controle Especial — Imprima em 2 vias: a 1ª fica retida
                                  na farmácia, a 2ª é do paciente.
                                </Typography>
                                <Tooltip
                                  title="Os blocos 'Identificação do Comprador' e 'Identificação do Fornecedor' são preenchidos na farmácia — saem em branco, é o esperado."
                                  arrow
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <InfoOutlinedIcon
                                    sx={{
                                      fontSize: '0.95rem',
                                      color: 'text.disabled',
                                      cursor: 'default',
                                      flexShrink: 0,
                                    }}
                                  />
                                </Tooltip>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {p.signed ? (
                            <Tooltip title={`${p.signerName} · ${p.signedAt}`}>
                              <Chip
                                size="small"
                                color="success"
                                variant="outlined"
                                icon={<VerifiedUserOutlinedIcon />}
                                label="Assinado"
                              />
                            </Tooltip>
                          ) : (
                            <Chip size="small" variant="outlined" label="Não assinado" />
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {p.signed ? (
                            <Tooltip title="Abrir PDF assinado">
                              <IconButton
                                size="small"
                                color="primary"
                                aria-label="abrir PDF assinado"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openSignedDocument(p.receituarioId);
                                }}
                              >
                                <PictureAsPdfOutlinedIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip
                              title={
                                hasControlledMedicines(p)
                                  ? CONTROLLED_SIGNATURE_UNAVAILABLE
                                  : 'Assinar digitalmente'
                              }
                            >
                              {/* span keeps the tooltip working on the disabled button */}
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  aria-label="assinar digitalmente"
                                  disabled={hasControlledMedicines(p)}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openSignModal(p);
                                  }}
                                >
                                  <DrawOutlinedIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              printPrescription(p);
                            }}
                            color="primary"
                            disabled={isTemplateLoading}
                          >
                            <PrintIcon />
                          </IconButton>
                          <IconButton size="small" onClick={toggleExpand}>
                            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={4} sx={{ py: 0, border: isExpanded ? undefined : 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                px: 2,
                                py: 1.5,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                              }}
                            >
                              {(p.medicamentos ?? []).map((med, idx) => (
                                <Box
                                  key={idx}
                                  sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {med.nome}
                                    </Typography>
                                    {med.controlled && (
                                      <Chip
                                        label="Controlado"
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                        sx={{ height: 18, fontSize: '0.65rem' }}
                                      />
                                    )}
                                  </Box>
                                  {med.descricaoQuantidade && (
                                    <Typography variant="caption" color="text.secondary">
                                      {med.descricaoQuantidade}
                                    </Typography>
                                  )}
                                  {med.posologia && (
                                    <Typography
                                      variant="caption"
                                      color="text.disabled"
                                      sx={{ whiteSpace: 'pre-wrap' }}
                                    >
                                      {med.posologia}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <SignHealthDocumentModal
        open={!!prescriptionToSign}
        onClose={closeSignModal}
        onSigned={onSigned}
        onSign={(certificatePassword) =>
          signPrescription(patientId, prescriptionToSign.receituarioId, certificatePassword)
        }
        title="Assinar receituário digitalmente"
        summary={formatPrescriptionSummary(prescriptionToSign)}
      />

      <PrescriptionModalRouter
        open={isModalOpen}
        onClose={() => setModalOpen(false)}
        onSave={onSavePrescription}
      />
    </>
  );
}
